import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import Icon from '../../../_shared/components/Icon';
import logocarfax from '../../../assets/external/logo-carfax.jpg';
import Loading from '../../../_shared/components/Loading';

import {
  VehicleDetailsContainer,
  Section,
  Title,
  SectionContainer,
  Specification,
  SpecificationInfoContainer,
  SpecificationLabel,
  SpecificationValue,
  StyledButton,
  VhrLogo,
  FeaturesContainer,
  Category,
  CategoryTitle,
  FeatureLabel,
  FeatureContainer,
  Feature,
  Description
} from './Styles';
import RetailTradeDetails from '../../../_shared/models/RetailTradeDetails';

const VehicleDetails = ({ tradeDetails, metadata, features }) => {
  const vehicleSpecs = new RetailTradeDetails(tradeDetails)?.specifications;
  const [vehicleFeatures, setVehicleFeatures] = useState([
    {
      category: 'Entertainment',
      features: [],
      id: 'ENTERTAINMENT',
      icon: 'TR5-Entertainment'
    },
    {
      category: 'Safety',
      features: [],
      id: 'SAFETY',
      icon: 'TR5-Safety'
    },
    {
      category: 'Interior',
      features: [],
      id: 'INTERIOR',
      icon: 'TR5-Vehicle-Seat'
    },
    {
      category: 'Exterior',
      features: [],
      id: 'EXTERIOR',
      icon: 'TR5-Exterior'
    },
    {
      category: 'Mechanical',
      features: [],
      id: 'MECHANICAL',
      icon: 'TR5-Wrench'
    }
  ]);

  useEffect(() => {
    if (features?.length) {
      const featuresCopy = [...features];
      const sortedFeatures = {};

      featuresCopy.forEach(feature => {
        if (!Object.keys(sortedFeatures).includes(feature.category))
          sortedFeatures[feature.category] = [];

        if (
          !sortedFeatures[feature.category].includes(
            feature.label || feature.chromeLabel
          )
        )
          sortedFeatures[feature.category].push(
            feature.label || feature.chromeLabel
          );
      });

      setVehicleFeatures(currentFeatures => {
        const currentCopy = [...currentFeatures];
        currentCopy.forEach(category => {
          category.features = sortedFeatures[category.id]?.sort();
        });
        return currentCopy;
      });
    }
  }, [features]);

  const openVhrReport = () => {
    window.open(tradeDetails?.vehicleHistoryReportUrl, '_blank');
  };

  return tradeDetails ? (
    <VehicleDetailsContainer>
      <Section id="vehicle-overview">
        <Title>
          <FormattedMessage id="vdp.overview.media.overview" />
        </Title>
        <SectionContainer>
          {vehicleSpecs?.map(feature => (
            <Specification key={`vehicle-spec-${feature.id}`}>
              <Icon icon={feature.icon} />
              <SpecificationInfoContainer>
                <SpecificationLabel
                  data-testid={`retailVdp-${feature.id}-label`}
                >
                  {feature.label}
                </SpecificationLabel>
                <SpecificationValue
                  data-testid={`retailVdp-${feature.id}-value`}
                >
                  {feature.value}
                </SpecificationValue>
              </SpecificationInfoContainer>
            </Specification>
          ))}
        </SectionContainer>
      </Section>
      {tradeDetails?.vehicleHistoryReportUrl && (
        <Section id="vehicle-vhr">
          <Title>
            <FormattedMessage id="vdp.retail.vhr" />
          </Title>
          <FormattedMessage id="vdp.retail.vhr.carfax" />
          <StyledButton
            data-testid="retailVdp-carfax-button"
            onClick={openVhrReport}
            enabled={tradeDetails?.vehicleHistoryReportUrl}
          >
            <FormattedMessage id="vdp.history.viewButton" />
            <VhrLogo src={logocarfax} alt="CarFax" />
          </StyledButton>
        </Section>
      )}
      <Section id="vehicle-features">
        <Title>
          <FormattedMessage id="retail.features" />
        </Title>
        <FeaturesContainer hasFeatures={features?.length > 0}>
          {features?.length > 0 &&
            vehicleFeatures.map(category => (
              <React.Fragment key={category.id}>
                {category.features?.length > 0 && (
                  <li>
                    <Category>
                      <CategoryTitle>
                        <Icon icon={category.icon} />
                        <FeatureLabel>{category.category}</FeatureLabel>
                      </CategoryTitle>
                      <FeatureContainer>
                        {category.features?.map(feature => (
                          <Feature key={`feature-${feature.replace(' ', '')}`}>
                            {feature}
                          </Feature>
                        ))}
                      </FeatureContainer>
                    </Category>
                  </li>
                )}
              </React.Fragment>
            ))}
          {!features?.length &&
            tradeDetails?.vehicleOptions
              ?.split(',')
              .map(feature => (
                <Feature key={`feature-${feature.replace(' ', '')}`}>
                  {feature}
                </Feature>
              ))}
        </FeaturesContainer>
      </Section>
      {metadata?.notes && (
        <Section id="vehicle-description">
          <Title>
            <FormattedMessage id="retail.description" />
          </Title>
          <Description data-testid="retailVdp-notes">
            {metadata.notes}
          </Description>
        </Section>
      )}
    </VehicleDetailsContainer>
  ) : (
    <VehicleDetailsContainer>
      <Loading width="300px" height="100px" />
      <Loading width="300px" height="100px" />
      <Loading width="300px" height="100px" />
    </VehicleDetailsContainer>
  );
};

export default VehicleDetails;
